import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import { withCodeNavCacheSqlite } from '../../../sqlite/code-nav-cache/database.js';
import { runCodeNavCacheWriteTransaction } from '../../../sqlite/code-nav-cache/code-nav-cache-sqlite-transaction.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from '../../yield-during-work.js';
import { createWalkYieldState, walkCodeNavFilesAsync } from '../../walk-code-nav-files.js';
import {
  clearSymbolIndexParentDataBatch,
  getSymbolIndexParentContext,
  upsertSymbolIndexParentPath,
} from '../sqlite/symbol-index-sqlite.js';
import { withSymbolIndexSqliteLock } from '../sqlite/symbol-index-sqlite-lock.js';
import { indexOneSymbolFileForBuild } from './ensure-symbol-index-file-impl.js';

const SYMBOL_PARENT_CLEAR_BATCH_SIZE = 256;

/**
 * Stream one file at a time — workspace paths kept in a string Set only (not full tag arrays).
 */
export async function buildSymbolIndexImpl(parentPath: string): Promise<{ fileCount: number; tagCount: number }> {
  const resolved = normalizeResolvedPath(parentPath);

  let clearRemaining = true;
  while (clearRemaining) {
    await withSymbolIndexSqliteLock(async () => {
      clearRemaining = await withCodeNavCacheSqlite((db) =>
        runCodeNavCacheWriteTransaction(db, () => {
          upsertSymbolIndexParentPath(db, resolved);
          const ctx = getSymbolIndexParentContext(db, resolved);
          return ctx == null ? false : clearSymbolIndexParentDataBatch(db, ctx, SYMBOL_PARENT_CLEAR_BATCH_SIZE);
        }),
      );
    });
  }

  const workspaceFiles = new Set<string>();
  const walkState = createWalkYieldState();
  const yieldState = createCodeNavYieldState();

  await walkCodeNavFilesAsync(
    resolved,
    async (relPath) => {
      await yieldDuringCodeNavWork(yieldState);
      workspaceFiles.add(relPath);
    },
    walkState,
  );

  let fileCount = 0;
  await walkCodeNavFilesAsync(
    resolved,
    async (relPath) => {
      await yieldDuringCodeNavWork(yieldState);
      await indexOneSymbolFileForBuild(resolved, relPath, workspaceFiles);
      fileCount += 1;
    },
    walkState,
  );

  return { fileCount, tagCount: 0 };
}
